import { LitElement, html, css, nothing } from "lit";
import type { PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";

/**
 * Visual and semantic weight of the dialog. `destructive` is for dialogs whose
 * confirm button causes an outage or destroys data — a restart-class publish
 * (`SC-F606`), an instance deletion, a key revocation.
 */
export type BnModalTone = "default" | "destructive";

/** Elements that are natively tabbable. `[tabindex]` matches are filtered by tabIndex >= 0. */
const TABBABLE_SELECTOR = [
  "a[href]",
  "area[href]",
  "button",
  "input",
  "select",
  "textarea",
  "iframe",
  "audio[controls]",
  "video[controls]",
  "[contenteditable]",
  "[tabindex]",
].join(",");

function isTabbable(el: Element): el is HTMLElement {
  if (!(el instanceof HTMLElement)) return false;
  if (el.hasAttribute("disabled")) return false;
  if (el.getAttribute("aria-hidden") === "true") return false;
  if (el.tabIndex < 0) return false;
  return el.getClientRects().length > 0;
}

/**
 * Walks the *flattened* tree (through slots and into shadow roots) collecting
 * tabbables in tab order. Walking into shadow roots is what makes the trap work
 * at all: a slotted <bn-button> is not itself focusable — the native <button>
 * inside its shadow root is.
 */
function collectTabbable(node: Element, out: HTMLElement[]): void {
  if (node instanceof HTMLSlotElement) {
    for (const assigned of node.assignedElements({ flatten: true })) collectTabbable(assigned, out);
    return;
  }
  if (node.matches(TABBABLE_SELECTOR) && isTabbable(node)) out.push(node);
  if (node.shadowRoot) {
    for (const child of Array.from(node.shadowRoot.children)) collectTabbable(child, out);
    return;
  }
  for (const child of Array.from(node.children)) collectTabbable(child, out);
}

/** The innermost active element, following shadow roots. */
function deepActiveElement(): HTMLElement | null {
  let el: Element | null = document.activeElement;
  while (el?.shadowRoot?.activeElement) el = el.shadowRoot.activeElement;
  return el instanceof HTMLElement ? el : null;
}

/**
 * bn-modal is the dialog shell: scrim, centred card, Escape and backdrop-click
 * to close, focus trapped inside the card while it is open and returned to the
 * opener when it closes, and the page behind it held still. It is *controlled* —
 * set `open` and listen for `close` to clear it; the modal never closes itself
 * behind your back. The scroll lock is released unconditionally on teardown,
 * including when the dialog is removed from the DOM while still open.
 *
 * The `consequence` slot exists for the publish confirmation (`SC-F606`,
 * `SC-F607`): the blast-radius statement renders between the body and the
 * footer, so it is read *before* the confirm button, in a tinted block that is
 * plainly not body copy. `tone="destructive"` makes that block error-coloured
 * and marks the card, for restart-class publishes and for deletions.
 *
 * Properties:
 * - `open` — reflected; the dialog renders nothing when false.
 * - `heading` — the dialog's accessible name.
 * - `tone` — `default` | `destructive`.
 * - `width` — card max width in px (default 480).
 *
 * Slots:
 * - default — body copy.
 * - `consequence` — the blast-radius statement, before the action row.
 * - `footer` — the action row; the confirm button goes last.
 *
 * Events:
 * - `close` — Escape, the close button, or a backdrop click. Composed.
 *
 *   <bn-modal ?open=${show} tone="destructive" heading="Publish generation 12?"
 *             @close=${() => (this.show = false)}>
 *     <p>Two policies and one datasource changed.</p>
 *     <p slot="consequence">
 *       This is a restart-class publish. The instance is unavailable for
 *       roughly 10–25 seconds and in-flight queries are cut short.
 *     </p>
 *     <bn-button slot="footer" @click=${() => (this.show = false)}>Cancel</bn-button>
 *     <bn-button slot="footer" variant="danger" @click=${this.publish}>Publish</bn-button>
 *   </bn-modal>
 */
@customElement("bn-modal")
export class BnModal extends LitElement {
  /** Whether the dialog is shown. Controlled by the owner. */
  @property({ type: Boolean, reflect: true }) open = false;
  /** The dialog's title and accessible name. */
  @property() heading = "";
  /** `destructive` for dialogs whose confirm button causes an outage or data loss. */
  @property({ reflect: true }) tone: BnModalTone = "default";
  /** Card max width in px. */
  @property({ type: Number }) width = 480;

  @state() private hasConsequence = false;
  @state() private hasFooter = false;

  /** What had focus before the dialog opened, so it can be given back. */
  private opener: HTMLElement | null = null;
  /** The page's own `body.style.overflow`, held while the scroll lock is on. */
  private lockedOverflow: string | null = null;

  static override styles = css`
    :host {
      font-family: var(--bino-font-sans);
    }
    .backdrop {
      position: fixed;
      inset: 0;
      background: var(--bino-scrim);
      z-index: var(--bino-z-modal);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: var(--bino-space-md);
    }
    .modal {
      background: var(--bino-surface);
      border-radius: var(--bino-radius-lg);
      box-shadow: var(--bino-shadow-page);
      width: 100%;
      max-height: calc(100vh - 2 * var(--bino-space-xl));
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
    .modal:focus {
      outline: none;
    }
    .modal.destructive {
      border-top: 3px solid var(--bino-error);
    }
    .head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--bino-space-md);
      padding: var(--bino-space-md) var(--bino-space-lg);
      border-bottom: 1px solid var(--bino-border);
      flex-shrink: 0;
    }
    .title {
      margin: 0;
      font-size: var(--bino-font-size-md);
      font-weight: 600;
      color: var(--bino-text);
    }
    .close {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.5rem;
      height: 1.5rem;
      background: none;
      border: 1px solid transparent;
      border-radius: var(--bino-radius-sm);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-lg);
      line-height: 1;
      cursor: pointer;
      color: var(--bino-text-secondary);
      transition: color var(--bino-transition-fast), background var(--bino-transition-fast);
    }
    .close:hover {
      background: var(--bino-surface-hover);
      color: var(--bino-text);
    }
    .close:focus-visible {
      outline: none;
      border-color: var(--bino-primary);
      box-shadow: 0 0 0 3px var(--bino-focus-ring);
    }
    .body {
      padding: var(--bino-space-lg);
      overflow-y: auto;
      color: var(--bino-text);
      font-size: var(--bino-font-size-md);
    }
    /* The consequence block. It is deliberately not body copy: tinted, hairlined
       top and bottom, and heavier, so the blast radius cannot be skimmed past on
       the way to the confirm button. */
    .consequence {
      flex-shrink: 0;
      padding: var(--bino-space-md) var(--bino-space-lg);
      border-top: 1px solid var(--bino-warning-border);
      border-bottom: 1px solid var(--bino-warning-border);
      background: var(--bino-warning-bg);
      color: var(--bino-warning-text);
      font-size: var(--bino-font-size-md);
      font-weight: 600;
      line-height: 1.45;
    }
    .modal.destructive .consequence {
      border-top-color: var(--bino-error-border);
      border-bottom-color: var(--bino-error-border);
      background: var(--bino-error-bg);
      color: var(--bino-text);
    }
    .footer {
      display: flex;
      justify-content: flex-end;
      gap: var(--bino-space-sm);
      padding: var(--bino-space-md) var(--bino-space-lg);
      border-top: 1px solid var(--bino-border);
      flex-shrink: 0;
    }
    .hidden {
      display: none;
    }
  `;

  private onDocumentKeydown = (e: KeyboardEvent) => {
    if (this.open && e.key === "Escape") {
      e.stopPropagation();
      this.requestClose();
    }
  };

  override connectedCallback() {
    super.connectedCallback();
    document.addEventListener("keydown", this.onDocumentKeydown);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("keydown", this.onDocumentKeydown);
    // A dialog torn down while open must not leave the page unscrollable.
    this.unlockScroll();
    this.restoreFocus();
  }

  override updated(changed: PropertyValues<this>) {
    if (!changed.has("open")) return;
    if (this.open) {
      this.opener = deepActiveElement();
      this.lockScroll();
      // Focus the card itself rather than the first control: a confirmation
      // dialog must never open with its confirm button pre-armed, and focusing
      // the container gets the whole dialog announced.
      this.card()?.focus();
    } else {
      this.unlockScroll();
      this.restoreFocus();
    }
  }

  private card(): HTMLElement | null {
    return this.renderRoot.querySelector<HTMLElement>(".modal");
  }

  private restoreFocus() {
    const opener = this.opener;
    this.opener = null;
    if (opener?.isConnected) opener.focus();
  }

  /**
   * Holds the page still behind the scrim. Without this the document keeps
   * scrolling under a wheel or an arrow key while a confirmation is open, which
   * moves the content the dialog is asking about out from behind it. bn-drawer
   * does the same, and both release unconditionally on teardown so a dialog
   * removed from the DOM while open cannot strand an unscrollable page.
   */
  private lockScroll() {
    if (this.lockedOverflow !== null) return;
    this.lockedOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }

  private unlockScroll() {
    if (this.lockedOverflow === null) return;
    document.body.style.overflow = this.lockedOverflow;
    this.lockedOverflow = null;
  }

  private requestClose() {
    this.dispatchEvent(new CustomEvent("close", { bubbles: true, composed: true }));
  }

  private onBackdropClick(e: Event) {
    // Only a click on the scrim itself, never one that bubbled out of the card.
    if (e.target === e.currentTarget) this.requestClose();
  }

  private onKeydown = (e: KeyboardEvent) => {
    if (e.key !== "Tab") return;
    const card = this.card();
    if (!card) return;
    const stops: HTMLElement[] = [];
    collectTabbable(card, stops);
    if (stops.length === 0) {
      e.preventDefault();
      return;
    }
    const index = stops.indexOf(deepActiveElement() as HTMLElement);
    if (e.shiftKey) {
      if (index <= 0) {
        e.preventDefault();
        stops[stops.length - 1].focus();
      }
      return;
    }
    if (index === -1 || index === stops.length - 1) {
      e.preventDefault();
      stops[0].focus();
    }
  };

  private onSlotChange(e: Event) {
    const slot = e.target as HTMLSlotElement;
    const filled = slot
      .assignedNodes({ flatten: true })
      .some((n) => n.nodeType === Node.ELEMENT_NODE || (n.textContent ?? "").trim() !== "");
    if (slot.name === "consequence") this.hasConsequence = filled;
    else this.hasFooter = filled;
  }

  override render() {
    if (!this.open) return nothing;
    const destructive = this.tone === "destructive";
    return html`
      <div class="backdrop" @click=${this.onBackdropClick} @keydown=${this.onKeydown}>
        <div
          class="modal ${destructive ? "destructive" : ""}"
          style="max-width:${this.width}px"
          role="dialog"
          aria-modal="true"
          aria-labelledby=${this.heading ? "modal-title" : nothing}
          aria-label=${this.heading ? nothing : "Dialog"}
          aria-describedby=${this.hasConsequence ? "modal-consequence" : nothing}
          tabindex="-1"
        >
          <div class="head">
            <h2 class="title" id="modal-title">${this.heading}</h2>
            <button
              class="close"
              type="button"
              title="Close"
              aria-label="Close"
              @click=${this.requestClose}
            >
              &times;
            </button>
          </div>
          <div class="body"><slot></slot></div>
          <div
            class="consequence ${this.hasConsequence ? "" : "hidden"}"
            id="modal-consequence"
            role="note"
          >
            <slot name="consequence" @slotchange=${this.onSlotChange}></slot>
          </div>
          <div class="footer ${this.hasFooter ? "" : "hidden"}">
            <slot name="footer" @slotchange=${this.onSlotChange}></slot>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bn-modal": BnModal;
  }
}
